import { useEffect, useMemo, useState } from 'react';

import { Role } from '@/features/auth/types';
import {
    Outlet,
    createRootRoute,
    useNavigate,
    useRouterState,
    useSearch,
} from '@tanstack/react-router';
import Cookies from 'js-cookie';
import { Toaster } from '@/components/ui/sonner';

import { sanitizeRedirect } from '@/lib/urlUtils';

import { useAuthData, useCurrentUser } from '@/hooks/useApiKey';
import { useDatadogUser } from '@/hooks/useDatadogUser';
import { useHasTeam } from '@/hooks/useHasTeam';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';

import { AppSidebar } from '@/components/app-sidebar';
import { LoadingScreen } from '@/components/loading-screen';
import { NoTeamSidebar } from '@/components/no-team-sidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { TableSortingFlagProvider } from '@/components/TableSortingFlagProvider';

export const Route = createRootRoute({
    component: RootRoute,
});

function RootRoute() {
    const router = useRouterState();
    const navigate = useNavigate();
    const { redirect: redirectParam } = useSearch({ strict: false });
    const [isLoading, setIsLoading] = useState(true);

    const { accessToken } = useAuthData();
    const [user, _, userLoading] = useCurrentUser();
    const hasTeam = useHasTeam();

    const isLoadingRoute = useMemo(() => {
        return isLoading || userLoading;
    }, [isLoading, userLoading]);

    const isPendingUser = useMemo(() => {
        return (
            user &&
            user.roles.length === 1 &&
            user.roles[0] === Role.pending &&
            user.teamId
        );
    }, [user]);

    useTokenRefresh();
    useDatadogUser();

    // TODO: Refactor this to use a more robust auth solution; possibly with context and tanstack router
    useEffect(() => {
        if (router.status === 'pending') return;

        setIsLoading(userLoading);

        if (router.location.pathname === '/coming-soon') return;

        if (userLoading) return;

        if (!user && !accessToken && router.location.pathname !== '/auth') {
            setIsLoading(true);
            const safeRedirect = sanitizeRedirect(router.location.href);
            navigate({
                to: '/auth',
                search: safeRedirect ? { redirect: safeRedirect } : {},
            });
            return;
        }

        if (user && !user.teamId && router.location.pathname !== '/teams') {
            setIsLoading(true);
            navigate({ to: '/teams' });
            return;
        }

        if (isPendingUser && router.location.pathname !== '/pending') {
            setIsLoading(true);
            navigate({ to: '/pending' });
            return;
        }

        if (
            user &&
            !isPendingUser &&
            user.teamId &&
            router.location.pathname === '/pending'
        ) {
            setIsLoading(true);
            navigate({ to: '/approved' });
            return;
        }

        if (
            user &&
            !user.roles.some((role: Role) => role === Role.pending) &&
            user.teamId &&
            router.location.pathname === '/auth'
        ) {
            setIsLoading(true);
            const safeRedirect = sanitizeRedirect(redirectParam);
            if (safeRedirect) {
                navigate({ href: safeRedirect, replace: true });
            } else {
                navigate({ to: '/', replace: true });
            }
            return;
        }
    }, [
        user,
        accessToken,
        userLoading,
        router,
        router.location.pathname,
        navigate,
        isPendingUser,
        redirectParam,
    ]);

    const noSidebarRoutes = ['/auth', '/coming-soon', '/pending', '/approved'];
    const showSidebar = !noSidebarRoutes.includes(router.location.pathname);
    const defaultOpen = Cookies.get('sidebar:state') === 'true';

    if (isLoadingRoute) return <LoadingScreen />;

    return (
        <SidebarProvider defaultOpen={defaultOpen}>
            <div className="flex flex-row h-screen w-full">
                {showSidebar ? (
                    hasTeam && !isPendingUser ? (
                        <AppSidebar />
                    ) : (
                        <NoTeamSidebar />
                    )
                ) : null}
                <div
                    className={`${showSidebar ? 'flex-1' : 'w-full'} min-w-0 overflow-auto`}
                >
                    <Toaster position="top-right" />
                    <TableSortingFlagProvider>
                        <Outlet />
                    </TableSortingFlagProvider>
                </div>
            </div>
        </SidebarProvider>
    );
}
